import {
  View,
  StyleSheet,
  ScrollView,
  Text,
  TouchableOpacity,
  Dimensions,
  TextInput,
  useWindowDimensions,
  Platform,
} from "react-native";
import SvaraModal from "./SvaraModal";
import { useState, useEffect } from "react";
import {
  BtnSidebarClosePng,
  InvitedPlayerBackground,
  InvitesTitlePng,
  ProfileRegistration,
  ProfileImage,
} from "../../../assets";
import { useDevice } from "../../../utils/hooks/useDevice";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { env } from "../../../utils/env";
import InvitedPlayerForTableCard from "./InviteModalComponents/InvitedPlayerForTable";
import { getSocket } from "../../../services/socket";

interface IProps {
  onClose: () => void;
  tableId?: number | string;
  tableName?: string;
}

const PLAYERS_PER_PAGE = 5;

function InvitePlayerToTableModal({ onClose, tableId }: IProps) {
  const { isDesktop } = useDevice();
  const [searchQuery, setSearchQuery] = useState("");
  const { height: SCREEN_HEIGHT } = Dimensions.get("window");
  const auth = useSelector((s: RootState) => s.auth);

  // Get socket using the proper service
  const socket =
    auth.user?.id && auth.user?.username
      ? getSocket(String(auth.user.id), String(auth.user.username))
      : null;
  const [inviteSent, setInviteSent] = useState(false);
  const [displayCount, setDisplayCount] = useState(PLAYERS_PER_PAGE);
  const activePlayers = useSelector(
    (s: RootState) => s.activePlayers.activePlayers,
  );
  // Ensure activePlayers is an array before filtering
  const safeActivePlayers = Array.isArray(activePlayers) ? activePlayers : [];
  const filteredPlayers = safeActivePlayers.filter((player) =>
    player?.username?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const displayedPlayers = filteredPlayers.slice(0, displayCount);
  const hasMore = displayCount < filteredPlayers.length;

  // Reset displayCount when search query changes
  useEffect(() => {
    setDisplayCount(PLAYERS_PER_PAGE);
  }, [searchQuery]);

  const getAvatarUrl = (avatarLink: string | null) => {
    if (!avatarLink) return null;
    const baseUrl = env("SVARA_API_URL") || "http://localhost:9000";
    const baseAvatarLink = `${baseUrl}/api/files/${avatarLink}`;
    return `${baseUrl}/api/files/${avatarLink}`;
  };

  const handleInvite = (inviteeId: number) => {
    console.log("socket", socket);
    console.log("tableId", tableId);
    if (!socket || !tableId) {
      console.error("Socket or tableId not available");
      alert("Cannot send invite at this time");
      return;
    }

    const invitePayload = {
      inviter: auth.user?.id,
      invitee: inviteeId,
      tableId: tableId,
    };

    console.log("Sending invite via socket:", invitePayload);
    socket.emit("svara:inviteUser", invitePayload);
    setInviteSent(true);
    // Optional: Show success message
    // alert(`Invite sent to player!`);
  };

  const handleLoadMore = () => {
    setDisplayCount((prev) =>
      Math.min(prev + PLAYERS_PER_PAGE, filteredPlayers.length),
    );
  };
  const { width } = useWindowDimensions();
  const isMobile = width < 600;
  const raw = width * 0.6;
  const titleWidth = Math.max(250, raw);

  return (
    <SvaraModal
      visible
      onClose={onClose}
      // backgroundImage={ProfileRegistration}
      titleImage={InvitesTitlePng}
      titleImageStyle={
        isMobile ? { top: -54, width: 240, height: 90 } : undefined
      }
      closeIcon={BtnSidebarClosePng}
      closeButtonSize="big"
      closeButtonStyles={{
        top: isDesktop ? 2 : 8,
        right: isDesktop ? 8 : 12,
        width: isDesktop ? 40 : 20,
        height: isDesktop ? 40 : 20,
      }}
      contentWrapperStyles={{
        alignItems: "stretch",
        paddingHorizontal: 0,
        paddingTop: 24,
        minHeight: isDesktop ? SCREEN_HEIGHT * 0.6 : SCREEN_HEIGHT * 0.5,
      }}
      containerStyles={{
        maxWidth: isDesktop ? 620 : "90%",
        width: isDesktop ? 620 : width * 0.8,
      }}
    >
      <View style={styles.modalContent}>
        {/* Search Bar - Fixed at top */}
        <View
          style={[
            styles.searchContainer,
            {
              maxWidth: isDesktop ? 300 : 200,
              marginLeft: isDesktop ? 30 : 20,
            },
          ]}
        >
          <TextInput
            style={[
              styles.searchInput,
              {
                fontSize: isDesktop ? 16 : 14,
                height: isDesktop ? 45 : 38,
                width: width * 0.8,
                maxWidth: isDesktop ? 320 : 200,
                margin: "auto",
                marginLeft: isDesktop ? 10 : 20,
              },
            ]}
            placeholder="Search players..."
            placeholderTextColor="#999"
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
          {filteredPlayers.length > 0 && (
            <Text style={styles.countText}>
              Showing {displayedPlayers.length} of {filteredPlayers.length}{" "}
              players
            </Text>
          )}
        </View>

        {/* Players List - Scrollable */}
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={true}
          nestedScrollEnabled={true}
        >
          {filteredPlayers.length === 0 ? (
            <View style={styles.emptyState}>
              <Text style={styles.emptyText}>
                {searchQuery ? "No players found" : "No active players found"}
              </Text>
            </View>
          ) : (
            <>
              {displayedPlayers.map((player) => (
                <View key={player.id} style={{ marginBottom: 8 }}>
                  <InvitedPlayerForTableCard
                    name={player.username}
                    score={player.level}
                    chips={player.chips.toString()}
                    avatarImage={
                      getAvatarUrl(player.avatarLink)
                        ? { uri: getAvatarUrl(player.avatarLink) }
                        : ProfileImage
                    }
                    backgroundImage={InvitedPlayerBackground}
                    onInvite={() => handleInvite(player.id)}
                    inviteSent={inviteSent}
                  />
                </View>
              ))}

              {/* Load More Button */}
              {hasMore && (
                <TouchableOpacity
                  style={styles.loadMoreButton}
                  onPress={handleLoadMore}
                  activeOpacity={0.7}
                >
                  <Text style={styles.loadMoreText}>
                    Load More ({filteredPlayers.length - displayCount}{" "}
                    remaining)
                  </Text>
                </TouchableOpacity>
              )}
            </>
          )}
        </ScrollView>
      </View>
    </SvaraModal>
  );
}

const styles = StyleSheet.create({
  closeBtn: {
    position: "absolute",
    left: 10,
    top: -18,
    padding: 6,
  },
  closeText: {
    color: "#fff",
    fontSize: 20,
  },
  modalContent: {
    width: "100%",
    flex: 1,
    flexDirection: "column",
  },
  searchContainer: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
    backgroundColor: "transparent",
  },
  searchInput: {
    backgroundColor: "#FFF",
    borderRadius: 8,
    paddingHorizontal: 15,
    color: "#333",
    borderWidth: 2,
    borderColor: "#FFD700",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  countText: {
    color: "#FFD700",
    fontSize: 12,
    marginTop: 8,
    textAlign: "center",
    fontWeight: "600",
  },
  scrollView: {
    flex: 1,
    width: "100%",
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 16,
  },
  emptyState: {
    padding: 40,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyText: {
    color: "#FFF",
    fontSize: 16,
  },
  loadMoreButton: {
    backgroundColor: "#FFD700",
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 20,
    marginTop: 16,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 3,
    elevation: 5,
  },
  loadMoreText: {
    color: "#000",
    fontSize: 14,
    fontWeight: "bold",
  },
});

export default InvitePlayerToTableModal;
